import React, { useState, useMemo } from 'react'
import { Link as RouterLink, useNavigate } from 'react-router-dom'
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  ButtonGroup,
  Menu,
  IconButton,
  Chip,
  Grid,
  Tooltip,
  LinearProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  Popover,
} from '@mui/material'
import {
  Search as SearchIcon,
  Visibility as ViewIcon,
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  Archive as ArchiveIcon,
  Delete as DeleteIcon,
  Info as InfoIcon,
  ExpandMore as ExpandMoreIcon,
  Group as GroupIcon,
  ViewList as ViewListIcon,
  Add as AddIcon,
  ArrowDropDown as ArrowDropDownIcon,
  Refresh as RefreshIcon,
  AddCircleOutline as AddCircleOutlineIcon,
  EditOutlined as EditOutlinedIcon,
  Update as UpdateIcon,
  Edit as EditIcon,
  Merge as MergeIcon,
  CalendarMonth as CalendarIcon,
} from '@mui/icons-material'
import { DataGridPro, GridColDef, GridToolbar, GridRowSelectionModel } from '@mui/x-data-grid-pro'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { fr } from 'date-fns/locale'
import { format, addDays, endOfYear } from 'date-fns'
import { useProposals, useBulkApproveProposals, useBulkRejectProposals, useBulkArchiveProposals, useBulkDeleteProposals, useDeleteProposal, useAgents } from '@/hooks/useApi'
import { ProposalStatus, ProposalType } from '@/types'
import { proposalStatusLabels, proposalStatusColors, proposalTypeLabels, proposalTypeStyles, categoryLevel1Labels, categoryLevel2Labels, categoryLevel2ByLevel1 } from '@/constants/proposals'
import { ProposalSourceChip } from './proposals/components/ProposalSourceChip'

// Types pour le filtre de période
type DateFilterPreset = 'ALL' | '7_DAYS' | '30_DAYS' | '90_DAYS' | 'THIS_YEAR' | 'CUSTOM'
type EditionStatusFilter = 'ALL' | 'CONFIRMED' | 'TO_BE_CONFIRMED' | 'CANCELED'

const dateFilterPresetLabels: Record<DateFilterPreset, string> = {
  'ALL': 'Toutes les dates',
  '7_DAYS': '7 prochains jours',
  '30_DAYS': '30 prochains jours',
  '90_DAYS': '90 prochains jours',
  'THIS_YEAR': 'Cette année',
  'CUSTOM': 'Période personnalisée',
}

const getProposalTypeStyle = (type: ProposalType) => {
  return proposalTypeStyles[type] || {
    bgcolor: 'grey.600',
    color: 'white',
    borderColor: 'grey.700',
    '& .MuiChip-icon': {
      color: 'grey.800'
    }
  }
}

const getProposalTypeIcon = (type: ProposalType): React.ReactElement | undefined => {
  switch (type) {
    case 'NEW_EVENT':
      return <AddCircleOutlineIcon sx={{ fontSize: 16 }} />
    case 'EDITION_UPDATE':
      return <EditOutlinedIcon sx={{ fontSize: 16 }} />
    case 'EVENT_UPDATE':
      return <UpdateIcon sx={{ fontSize: 16 }} />
    case 'RACE_UPDATE':
      return <EditIcon sx={{ fontSize: 16 }} />
    default:
      return undefined
  }
}

const ProposalList: React.FC = () => {
  const navigate = useNavigate()
  const [searchTerm, setSearchTerm] = useState('')

  // Charger les filtres depuis localStorage au montage
  const [statusFilter, setStatusFilter] = useState<ProposalStatus | 'ALL'>(() => {
    const saved = localStorage.getItem('proposalStatusFilter')
    return (saved as ProposalStatus | 'ALL') || 'PENDING'
  })

  const [typeFilter, setTypeFilter] = useState<ProposalType | 'ALL'>(() => {
    const saved = localStorage.getItem('proposalTypeFilter')
    return (saved as ProposalType | 'ALL') || 'ALL'
  })

  const [agentFilter, setAgentFilter] = useState<string>(() => {
    const saved = localStorage.getItem('proposalAgentFilter')
    return saved || 'ALL'
  })

  const [categoryLevel1Filter, setCategoryLevel1Filter] = useState<string>(() => {
    const saved = localStorage.getItem('proposalCategoryLevel1Filter')
    return saved || 'ALL'
  })

  const [categoryLevel2Filter, setCategoryLevel2Filter] = useState<string>(() => {
    const saved = localStorage.getItem('proposalCategoryLevel2Filter')
    return saved || 'ALL'
  })

  const [editionStatusFilter, setEditionStatusFilter] = useState<EditionStatusFilter>(() => {
    const saved = localStorage.getItem('proposalEditionStatusFilter')
    return (saved as EditionStatusFilter) || 'ALL'
  })

  const [selectedRows, setSelectedRows] = useState<GridRowSelectionModel>([])
  const [viewMode, setViewMode] = useState<'grouped' | 'table'>('grouped')
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set())
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 50 })

  // Dropdown menu pour création proposition
  const [createMenuAnchor, setCreateMenuAnchor] = useState<null | HTMLElement>(null)
  const createMenuOpen = Boolean(createMenuAnchor)

  // Charger l'ordre de tri depuis localStorage au montage
  const [groupSort, setGroupSort] = useState<'date-asc' | 'date-desc' | 'created-desc'>(() => {
    const saved = localStorage.getItem('proposalGroupSort')
    return (saved as 'date-asc' | 'date-desc' | 'created-desc') || 'date-asc'
  })

  // Filtre par période de date (sur proposedStartDate)
  const [dateFilterPreset, setDateFilterPreset] = useState<DateFilterPreset>(() => {
    const saved = localStorage.getItem('proposalDateFilterPreset')
    return (saved as DateFilterPreset) || 'ALL'
  })
  const [customDateFrom, setCustomDateFrom] = useState<Date | null>(() => {
    const saved = localStorage.getItem('proposalCustomDateFrom')
    return saved ? new Date(saved) : null
  })
  const [customDateTo, setCustomDateTo] = useState<Date | null>(() => {
    const saved = localStorage.getItem('proposalCustomDateTo')
    return saved ? new Date(saved) : null
  })
  const [datePopoverAnchor, setDatePopoverAnchor] = useState<HTMLElement | null>(null)

  // Sauvegarder les filtres dans localStorage quand ils changent
  React.useEffect(() => {
    localStorage.setItem('proposalStatusFilter', statusFilter)
  }, [statusFilter])

  React.useEffect(() => {
    localStorage.setItem('proposalTypeFilter', typeFilter)
  }, [typeFilter])

  React.useEffect(() => {
    localStorage.setItem('proposalAgentFilter', agentFilter)
  }, [agentFilter])

  React.useEffect(() => {
    localStorage.setItem('proposalCategoryLevel1Filter', categoryLevel1Filter)
    // Reset categoryLevel2 si on change categoryLevel1
    if (categoryLevel1Filter === 'ALL') {
      setCategoryLevel2Filter('ALL')
    }
  }, [categoryLevel1Filter])

  React.useEffect(() => {
    localStorage.setItem('proposalCategoryLevel2Filter', categoryLevel2Filter)
  }, [categoryLevel2Filter])

  React.useEffect(() => {
    localStorage.setItem('proposalEditionStatusFilter', editionStatusFilter)
  }, [editionStatusFilter])

  // Sauvegarder l'ordre de tri dans localStorage quand il change
  React.useEffect(() => {
    localStorage.setItem('proposalGroupSort', groupSort)
  }, [groupSort])

  // Sauvegarder le filtre de date dans localStorage
  React.useEffect(() => {
    localStorage.setItem('proposalDateFilterPreset', dateFilterPreset)
  }, [dateFilterPreset])

  React.useEffect(() => {
    if (customDateFrom) {
      localStorage.setItem('proposalCustomDateFrom', customDateFrom.toISOString())
    } else {
      localStorage.removeItem('proposalCustomDateFrom')
    }
  }, [customDateFrom])

  React.useEffect(() => {
    if (customDateTo) {
      localStorage.setItem('proposalCustomDateTo', customDateTo.toISOString())
    } else {
      localStorage.removeItem('proposalCustomDateTo')
    }
  }, [customDateTo])

  // Calculer les dates de filtre effectives selon le preset
  const effectiveDateRange = useMemo(() => {
    const today = new Date()
    today.setHours(0, 0, 0, 0)

    switch (dateFilterPreset) {
      case '7_DAYS':
        return { from: today, to: addDays(today, 7) }
      case '30_DAYS':
        return { from: today, to: addDays(today, 30) }
      case '90_DAYS':
        return { from: today, to: addDays(today, 90) }
      case 'THIS_YEAR':
        return { from: today, to: endOfYear(today) }
      case 'CUSTOM':
        return { from: customDateFrom, to: customDateTo }
      default:
        return { from: null, to: null }
    }
  }, [dateFilterPreset, customDateFrom, customDateTo])

  // Debounce du terme de recherche pour éviter trop de requêtes API
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(searchTerm)

  React.useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm)
    }, 300) // 300ms de debounce
    return () => clearTimeout(timer)
  }, [searchTerm])

  // Charger la liste des agents pour le filtre
  const { data: agentsData } = useAgents({ includeInactive: true })
  const uniqueAgents = useMemo(() => {
    if (!agentsData?.data) return []
    return agentsData.data.map(a => a.name).sort()
  }, [agentsData?.data])

  const { data: proposalsData, isLoading, refetch } = useProposals(
    {
      status: statusFilter !== 'ALL' ? statusFilter : undefined,
      type: typeFilter !== 'ALL' ? typeFilter : undefined,
      agentName: agentFilter !== 'ALL' ? agentFilter : undefined,
      categoryLevel1: categoryLevel1Filter !== 'ALL' ? categoryLevel1Filter : undefined,
      categoryLevel2: categoryLevel2Filter !== 'ALL' ? categoryLevel2Filter : undefined,
      editionCalendarStatus: editionStatusFilter !== 'ALL' ? editionStatusFilter : undefined,
      search: debouncedSearchTerm.trim() || undefined,
      startDateFrom: effectiveDateRange.from ? format(effectiveDateRange.from, 'yyyy-MM-dd') : undefined,
      startDateTo: effectiveDateRange.to ? format(effectiveDateRange.to, 'yyyy-MM-dd') : undefined,
    },
    paginationModel.pageSize,
    paginationModel.page * paginationModel.pageSize,
    groupSort  // Tri côté serveur
  )

  // Reset pagination when filters or sort change
  React.useEffect(() => {
    setPaginationModel(prev => ({ ...prev, page: 0 }))
  }, [statusFilter, typeFilter, agentFilter, categoryLevel1Filter, categoryLevel2Filter, editionStatusFilter, groupSort, debouncedSearchTerm, dateFilterPreset, customDateFrom, customDateTo])

  const bulkApproveMutation = useBulkApproveProposals()
  const bulkRejectMutation = useBulkRejectProposals()
  const bulkArchiveMutation = useBulkArchiveProposals()
  const bulkDeleteMutation = useBulkDeleteProposals()
  const deleteMutation = useDeleteProposal()

  // Les propositions sont maintenant filtrées côté serveur
  const filteredProposals = proposalsData?.data || []

  // Group proposals by event/edition
  const groupedProposals = useMemo(() => {
    const groups: Record<string, typeof filteredProposals> = {}

    filteredProposals.forEach(proposal => {
      let groupKey: string
      if (proposal.type === 'NEW_EVENT') {
        groupKey = `new-event-${proposal.id}` // Chaque nouvel événement est dans son propre groupe
      } else if (proposal.type === 'EVENT_MERGE') {
        groupKey = `event-merge-${proposal.id}` // Chaque fusion est dans son propre groupe
      } else if (!proposal.eventId && !proposal.editionId) {
        // Propositions sans eventId ni editionId (ex: FFA Results sans match)
        // Chacune doit être dans son propre groupe pour éviter le regroupement massif
        groupKey = `unmatched-${proposal.id}`
      } else {
        groupKey = `${proposal.eventId || 'unknown'}-${proposal.editionId || 'unknown'}`
      }

      if (!groups[groupKey]) {
        groups[groupKey] = []
      }
      groups[groupKey].push(proposal)
    })

    // Trier les propositions dans chaque groupe par confiance décroissante
    Object.keys(groups).forEach(groupKey => {
      groups[groupKey].sort((a, b) => {
        const confidenceA = a.confidence || 0
        const confidenceB = b.confidence || 0
        return confidenceB - confidenceA
      })
    })

    return groups
  }, [filteredProposals])

  // Clés des groupes triées selon le paramètre groupSort
  // Pour date-asc/date-desc: on trie par la date min/max des propositions du groupe
  // Pour created-desc: on trie par la date de création la plus récente du groupe
  const sortedGroupKeys = useMemo(() => {
    const groupKeys = Object.keys(groupedProposals)

    // Calculer la date représentative pour chaque groupe
    const getGroupSortDate = (groupKey: string): Date | null => {
      const proposals = groupedProposals[groupKey]
      if (!proposals || proposals.length === 0) return null

      if (groupSort === 'created-desc') {
        // Pour created-desc, prendre la date de création la plus récente
        const dates = proposals
          .map(p => p.createdAt ? new Date(p.createdAt) : null)
          .filter((d): d is Date => d !== null && !isNaN(d.getTime()))
        if (dates.length === 0) return null
        return new Date(Math.max(...dates.map(d => d.getTime())))
      } else {
        // Pour date-asc/date-desc, utiliser proposedStartDate
        const dates = proposals
          .map(p => p.proposedStartDate ? new Date(p.proposedStartDate) : null)
          .filter((d): d is Date => d !== null && !isNaN(d.getTime()))
        if (dates.length === 0) return null
        // Pour date-asc, prendre la date la plus proche (min)
        // Pour date-desc, prendre la date la plus éloignée (max)
        if (groupSort === 'date-asc') {
          return new Date(Math.min(...dates.map(d => d.getTime())))
        } else {
          return new Date(Math.max(...dates.map(d => d.getTime())))
        }
      }
    }

    // Trier les groupes
    return groupKeys.sort((a, b) => {
      const dateA = getGroupSortDate(a)
      const dateB = getGroupSortDate(b)

      // Les groupes sans date vont à la fin
      if (!dateA && !dateB) return 0
      if (!dateA) return 1
      if (!dateB) return -1

      if (groupSort === 'date-asc') {
        return dateA.getTime() - dateB.getTime()
      } else if (groupSort === 'date-desc') {
        return dateB.getTime() - dateA.getTime()
      } else {
        // created-desc
        return dateB.getTime() - dateA.getTime()
      }
    })
  }, [groupedProposals, groupSort])

  const handleToggleGroup = (groupKey: string, event: React.MouseEvent) => {
    event.stopPropagation()
    setExpandedGroups(prev => {
      const newExpanded = new Set(prev)
      if (newExpanded.has(groupKey)) {
        newExpanded.delete(groupKey)
      } else {
        newExpanded.add(groupKey)
      }
      return newExpanded
    })
  }

  const getChangesSummary = (changes: Record<string, any>) => {
    const changeKeys = Object.keys(changes)
    if (changeKeys.length === 0) return 'Aucun changement'
    if (changeKeys.length === 1) {
      const key = changeKeys[0]
      return `Changement de ${key}`
    }
    return `Changements multiples (${changeKeys.length})`
  }

  const getEventTitle = (groupKey: string, proposals: any[]) => {
    if (groupKey.startsWith('new-event-')) {
      const proposal = proposals[0]
      // Extraire .new si c'est un objet change
      const eventNameValue = proposal.changes.eventName || proposal.changes.name
      const eventName = (typeof eventNameValue === 'object' && eventNameValue?.new) ? eventNameValue.new : eventNameValue
      return eventName || 'Nouvel événement'
    }

    if (groupKey.startsWith('event-merge-')) {
      const proposal = proposals[0]
      const merge = proposal.changes?.merge
      if (merge) {
        return `Fusion: ${merge.duplicateEventName} → ${merge.keepEventName}`
      }
      return 'Fusion d\'événements'
    }

    const proposal = proposals[0]

    // PRIORITÉ 1: Utiliser les champs enrichis par l'API (eventName, eventCity, editionYear)
    if (proposal.eventName) {
      const cityPart = proposal.eventCity ? ` - ${proposal.eventCity}` : ''
      const yearPart = proposal.editionYear ? ` ${proposal.editionYear}` : ''
      return `${proposal.eventName}${cityPart}${yearPart}`
    }

    // PRIORITÉ 2: Essayer d'extraire le nom depuis les métadonnées de justification
    if (proposal.justification && Array.isArray(proposal.justification)) {
      for (const justif of proposal.justification) {
        // Support eventName (standard) ou ffaName (FFA Results Agent)
        const eventName = justif.metadata?.eventName || justif.metadata?.ffaName
        if (eventName) {
          const eventCity = justif.metadata?.eventCity || justif.metadata?.ffaCity
          const year = justif.metadata?.editionYear

          // Construire le titre avec le nom, ville et année
          const cityPart = eventCity ? ` - ${eventCity}` : ''
          const yearPart = year ? ` ${year}` : ''
          return `${eventName}${cityPart}${yearPart}`
        }
      }
    }

    // Pour EDITION_UPDATE sans eventId, utiliser editionId avec les métadonnées si disponibles
    if (!proposal.eventId && proposal.editionId) {
      return `Édition ${proposal.editionId}`
    }

    if (!proposal.eventId) {
      return 'Événement inconnu'
    }

    // Fallback: extraire le nom de l'événement depuis l'ID (logique existante)
    let eventName = proposal.eventId.toString().replace('event_', '')

    // Nettoyer et formater le nom
    if (eventName.includes('marathon_paris')) {
      eventName = 'Marathon de Paris'
    } else if (eventName.includes('10km_boulogne')) {
      eventName = '10km de Boulogne'
    } else if (eventName.includes('semi_marathon_boulogne')) {
      eventName = 'Semi-marathon de Boulogne'
    } else {
      // Fallback: remplacer les underscores par des espaces et capitaliser
      eventName = eventName.replace(/_/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase())
    }

    // Extraire l'année depuis l'eventId ou editionId
    const yearMatch = (proposal.eventId + (proposal.editionId || '')).match(/202\d/)
    const year = yearMatch ? yearMatch[0] : ''

    return year ? `${eventName} ${year}` : eventName
  }

  const handleBulkApprove = async () => {
    if (selectedRows.length === 0) return
    await bulkApproveMutation.mutateAsync({
      proposalIds: selectedRows as string[]
    })
    setSelectedRows([])
  }

  const handleBulkReject = async () => {
    if (selectedRows.length === 0) return
    await bulkRejectMutation.mutateAsync({
      proposalIds: selectedRows as string[]
    })
    setSelectedRows([])
  }

  const handleBulkArchive = async () => {
    if (selectedRows.length === 0) return
    const count = selectedRows.length
    await bulkArchiveMutation.mutateAsync({
      proposalIds: selectedRows as string[],
      archiveReason: 'Archivage en lot depuis la liste des propositions'
    })
    setSelectedRows([])
    // Optionnel: afficher un message personnalisé
    console.log(`${count} propositions archivées - elles n'apparaissent plus dans le filtre "En attente"`)
  }

  const handleBulkDelete = async () => {
    if (selectedRows.length === 0) return
    if (!confirm(`Êtes-vous sûr de vouloir supprimer définitivement ${selectedRows.length} proposition(s) ? Cette action est irréversible.`)) {
      return
    }
    const count = selectedRows.length
    await bulkDeleteMutation.mutateAsync({
      proposalIds: selectedRows as string[]
    })
    setSelectedRows([])
    console.log(`${count} propositions supprimées définitivement de la base de données`)
  }

  const handleSingleArchive = async (proposalId: string) => {
    await bulkArchiveMutation.mutateAsync({
      proposalIds: [proposalId],
      archiveReason: 'Archivage individuel depuis la liste des propositions'
    })
  }

  const handleSingleDelete = async (proposalId: string) => {
    if (!confirm('Êtes-vous sûr de vouloir supprimer définitivement cette proposition ? Cette action est irréversible.')) {
      return
    }
    await deleteMutation.mutateAsync(proposalId)
  }

  const columns: GridColDef[] = [
    {
      field: 'eventInfo',
      headerName: 'Événement / Édition',
      width: 250,
      valueGetter: (params) => {
        const proposal = params.row
        // Pour les nouveaux événements
        if (proposal.type === 'NEW_EVENT') {
          // Extraire .new si c'est un objet change, sinon utiliser la valeur directement
          const nameValue = proposal.changes.name
          const eventNameValue = proposal.changes.eventName
          const name = (typeof nameValue === 'object' && nameValue?.new) ? nameValue.new : nameValue
          const eventName = (typeof eventNameValue === 'object' && eventNameValue?.new) ? eventNameValue.new : eventNameValue
          return name || eventName || 'Nouvel événement'
        }

        // PRIORITÉ 1: Utiliser les champs enrichis par l'API
        if (proposal.eventName) {
          const cityPart = proposal.eventCity ? ` - ${proposal.eventCity}` : ''
          const yearPart = proposal.editionYear ? ` ${proposal.editionYear}` : ''
          return `${proposal.eventName}${cityPart}${yearPart}`
        }

        // PRIORITÉ 2: Essayer d'extraire depuis les métadonnées de justification
        if (proposal.justification && Array.isArray(proposal.justification)) {
          for (const justif of proposal.justification) {
            // Support eventName (standard) ou ffaName (FFA Results Agent)
            const eventName = justif.metadata?.eventName || justif.metadata?.ffaName
            if (eventName) {
              const eventCity = justif.metadata?.eventCity || justif.metadata?.ffaCity
              const year = justif.metadata?.editionYear
              const cityPart = eventCity ? ` - ${eventCity}` : ''
              return year ? `${eventName}${cityPart} - ${year}` : `${eventName}${cityPart}`
            }
          }
        }

        // Fallback sur les IDs
        if (proposal.editionId) {
          return `Édition ${proposal.editionId}`
        }
        if (proposal.eventId) {
          return `Événement ${proposal.eventId}`
        }
        return 'Inconnu'
      },
      renderCell: (params) => (
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {params.value}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {proposalTypeLabels[params.row.type as ProposalType]}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'agent',
      headerName: 'Agent',
      width: 200,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <Typography variant="body2">
            {params.value.name}
          </Typography>
          <ProposalSourceChip
            source={(params.row as any).sourceMetadata?.type}
            milesEnv={(params.row as any).sourceMetadata?.extra?.milesEnv}
          />
        </Box>
      ),
    },
    {
      field: 'type',
      headerName: 'Type',
      width: 200,
      renderCell: (params) => (
        <Chip
          icon={getProposalTypeIcon(params.value as ProposalType)}
          size="small"
          label={proposalTypeLabels[params.value as ProposalType]}
          sx={getProposalTypeStyle(params.value as ProposalType)}
        />
      ),
    },
    {
      field: 'confidence',
      headerName: 'Confiance',
      width: 100,
      renderCell: (params) => (
        <Typography variant="body2" color="text.secondary">
          {params.value ? `${Math.round(params.value * 100)}%` : 'N/A'}
        </Typography>
      ),
    },
    {
      field: 'status',
      headerName: 'Statut',
      width: 120,
      renderCell: (params) => (
        <Chip
          size="small"
          label={proposalStatusLabels[params.value as ProposalStatus]}
          color={proposalStatusColors[params.value as ProposalStatus]}
        />
      ),
    },
    {
      field: 'createdAt',
      headerName: 'Date proposition',
      width: 140,
      renderCell: (params) => (
        <Typography variant="caption" color="text.secondary">
          {new Date(params.value).toLocaleString('fr-FR')}
        </Typography>
      ),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 160,
      sortable: false,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <Tooltip title="Voir détails">
            <IconButton
              size="small"
              component={RouterLink}
              to={`/proposals/${params.row.id}`}
            >
              <ViewIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {params.row.status === 'PENDING' && (
            <>
              <Tooltip title="Archiver">
                <IconButton
                  size="small"
                  color="warning"
                  onClick={() => handleSingleArchive(params.row.id)}
                  disabled={bulkArchiveMutation.isPending || deleteMutation.isPending}
                >
                  <ArchiveIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Supprimer">
                <IconButton
                  size="small"
                  color="error"
                  onClick={() => handleSingleDelete(params.row.id)}
                  disabled={bulkArchiveMutation.isPending || deleteMutation.isPending}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </>
          )}
        </Box>
      ),
    },
  ]

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        {/* Ligne 1: Titre + View Mode */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="h4" sx={{ fontWeight: 600 }}>
              Propositions
            </Typography>

            {/* View Mode Toggle */}
            <Box sx={{ display: 'flex', border: 1, borderColor: 'divider', borderRadius: 1 }}>
              <Button
                size="small"
                startIcon={<GroupIcon />}
                onClick={() => setViewMode('grouped')}
                variant={viewMode === 'grouped' ? 'contained' : 'text'}
                sx={{ borderRadius: 0 }}
              >
                Groupé
              </Button>
              <Button
                size="small"
                startIcon={<ViewListIcon />}
                onClick={() => setViewMode('table')}
                variant={viewMode === 'table' ? 'contained' : 'text'}
                sx={{ borderRadius: 0 }}
              >
                Tableau
              </Button>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {/* Bouton de rafraîchissement */}
            <Tooltip title="Rafraîchir">
              <IconButton
                onClick={() => refetch()}
                disabled={isLoading}
                color="primary"
              >
                <RefreshIcon />
              </IconButton>
            </Tooltip>

            {/* Créer une proposition button avec dropdown */}
            <ButtonGroup variant="contained" color="primary" size="small">
              <Button
                startIcon={<AddIcon />}
                onClick={() => navigate('/proposals/create')}
                sx={{ whiteSpace: 'nowrap' }}
              >
                Nouvelle proposition
              </Button>
              <Button
                size="small"
                onClick={(e) => setCreateMenuAnchor(e.currentTarget)}
                sx={{ px: 1 }}
              >
                <ArrowDropDownIcon />
              </Button>
            </ButtonGroup>
            <Menu
              anchorEl={createMenuAnchor}
              open={createMenuOpen}
              onClose={() => setCreateMenuAnchor(null)}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
            >
              <MenuItem
                onClick={() => {
                  setCreateMenuAnchor(null)
                  navigate('/proposals/create')
                }}
              >
                <AddIcon sx={{ mr: 1 }} fontSize="small" />
                Nouvel événement
              </MenuItem>
              <MenuItem
                onClick={() => {
                  setCreateMenuAnchor(null)
                  navigate('/proposals/create-existing')
                }}
              >
                <SearchIcon sx={{ mr: 1 }} fontSize="small" />
                Mise à jour édition
              </MenuItem>
              <Divider />
              <MenuItem
                onClick={() => {
                  setCreateMenuAnchor(null)
                  navigate('/events/merge')
                }}
              >
                <MergeIcon sx={{ mr: 1 }} fontSize="small" />
                Fusionner des doublons
              </MenuItem>
            </Menu>
          </Box>
        </Box>

        {/* Ligne 2: Bulk Actions (uniquement si sélection) */}
        {selectedRows.length > 0 && viewMode === 'table' && (
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              color="success"
              size="small"
              startIcon={<ApproveIcon />}
              onClick={handleBulkApprove}
              disabled={bulkApproveMutation.isPending || bulkRejectMutation.isPending || bulkArchiveMutation.isPending || bulkDeleteMutation.isPending}
            >
              ✓ ({selectedRows.length})
            </Button>
            <Button
              variant="contained"
              color="error"
              size="small"
              startIcon={<RejectIcon />}
              onClick={handleBulkReject}
              disabled={bulkApproveMutation.isPending || bulkRejectMutation.isPending || bulkArchiveMutation.isPending || bulkDeleteMutation.isPending}
            >
              ✗ ({selectedRows.length})
            </Button>
            <Button
              variant="outlined"
              color="warning"
              size="small"
              startIcon={<ArchiveIcon />}
              onClick={handleBulkArchive}
              disabled={bulkApproveMutation.isPending || bulkRejectMutation.isPending || bulkArchiveMutation.isPending || bulkDeleteMutation.isPending}
            >
              Archiver ({selectedRows.length})
            </Button>
            <Button
              variant="outlined"
              color="error"
              size="small"
              startIcon={<DeleteIcon />}
              onClick={handleBulkDelete}
              disabled={bulkApproveMutation.isPending || bulkRejectMutation.isPending || bulkArchiveMutation.isPending || bulkDeleteMutation.isPending}
            >
              Supprimer ({selectedRows.length})
            </Button>
          </Box>
        )}
      </Box>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          {/* Ligne 1: Recherche + Filtres principaux */}
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Rechercher"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
                size="small"
              />
            </Grid>
            <Grid item xs={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Statut</InputLabel>
                <Select
                  value={statusFilter}
                  label="Statut"
                  onChange={(e) => setStatusFilter(e.target.value as ProposalStatus | 'ALL')}
                >
                  <MenuItem value="ALL">Tous les statuts</MenuItem>
                  {Object.entries(proposalStatusLabels).map(([status, label]) => (
                    <MenuItem key={status} value={status}>
                      {label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Type</InputLabel>
                <Select
                  value={typeFilter}
                  label="Type"
                  onChange={(e) => setTypeFilter(e.target.value as ProposalType | 'ALL')}
                >
                  <MenuItem value="ALL">Tous les types</MenuItem>
                  {Object.entries(proposalTypeLabels).map(([type, label]) => (
                    <MenuItem key={type} value={type}>
                      {label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Agent</InputLabel>
                <Select
                  value={agentFilter}
                  label="Agent"
                  onChange={(e) => setAgentFilter(e.target.value)}
                >
                  <MenuItem value="ALL">Tous les agents</MenuItem>
                  {uniqueAgents.map((agentName) => (
                    <MenuItem key={agentName} value={agentName}>
                      {agentName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Statut édition</InputLabel>
                <Select
                  value={editionStatusFilter}
                  label="Statut édition"
                  onChange={(e) => setEditionStatusFilter(e.target.value as EditionStatusFilter)}
                >
                  <MenuItem value="ALL">Tous les statuts édition</MenuItem>
                  <MenuItem value="CONFIRMED">Confirmé</MenuItem>
                  <MenuItem value="TO_BE_CONFIRMED">À confirmer</MenuItem>
                  <MenuItem value="CANCELED">Annulé</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            {viewMode === 'grouped' && (
              <Grid item xs={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Tri</InputLabel>
                  <Select
                    value={groupSort}
                    label="Tri"
                    onChange={(e) => setGroupSort(e.target.value as 'date-asc' | 'date-desc' | 'created-desc')}
                  >
                    <MenuItem value="date-asc">startDate proche en premier</MenuItem>
                    <MenuItem value="date-desc">startDate éloignée en premier</MenuItem>
                    <MenuItem value="created-desc">createdAt récent en premier</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            )}
          </Grid>

          {/* Ligne 2: Filtres par catégorie et période */}
          <Grid container spacing={2} alignItems="center" sx={{ mt: 1 }}>
            <Grid item xs={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Catégorie</InputLabel>
                <Select
                  value={categoryLevel1Filter}
                  label="Catégorie"
                  onChange={(e) => setCategoryLevel1Filter(e.target.value)}
                >
                  <MenuItem value="ALL">Toutes catégories</MenuItem>
                  {Object.entries(categoryLevel1Labels).map(([value, label]) => (
                    <MenuItem key={value} value={value}>
                      {label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            {categoryLevel1Filter !== 'ALL' && categoryLevel2ByLevel1[categoryLevel1Filter]?.length > 0 && (
              <Grid item xs={6} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Sous-catégorie</InputLabel>
                  <Select
                    value={categoryLevel2Filter}
                    label="Sous-catégorie"
                    onChange={(e) => setCategoryLevel2Filter(e.target.value)}
                  >
                    <MenuItem value="ALL">Toutes</MenuItem>
                    {categoryLevel2ByLevel1[categoryLevel1Filter].map((value) => (
                      <MenuItem key={value} value={value}>
                        {categoryLevel2Labels[value] || value}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}

            {/* Filtre par période de date */}
            <Grid item xs={6} md={2}>
              <Button
                fullWidth
                variant="outlined"
                size="small"
                startIcon={<CalendarIcon />}
                onClick={(e) => setDatePopoverAnchor(e.currentTarget)}
                sx={{
                  height: 40,
                  justifyContent: 'flex-start',
                  textTransform: 'none',
                  color: dateFilterPreset !== 'ALL' ? 'primary.main' : 'text.secondary',
                  borderColor: dateFilterPreset !== 'ALL' ? 'primary.main' : 'divider',
                }}
              >
                {dateFilterPresetLabels[dateFilterPreset]}
              </Button>
              <Popover
                open={Boolean(datePopoverAnchor)}
                anchorEl={datePopoverAnchor}
                onClose={() => setDatePopoverAnchor(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
              >
                <Box sx={{ p: 2, minWidth: 250 }}>
                  <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                    Période de l'événement
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    {(['ALL', '7_DAYS', '30_DAYS', '90_DAYS', 'THIS_YEAR'] as const).map((preset) => (
                        <Button
                          key={preset}
                          variant={dateFilterPreset === preset ? 'contained' : 'text'}
                          size="small"
                          onClick={() => {
                            setDateFilterPreset(preset)
                            setDatePopoverAnchor(null)
                          }}
                          sx={{ justifyContent: 'flex-start', fontSize: '0.813rem', textTransform: 'none' }}
                        >
                          {dateFilterPresetLabels[preset]}
                        </Button>
                      ))}
                    <Divider sx={{ my: 0.5 }} />
                    <Button
                      variant={dateFilterPreset === 'CUSTOM' ? 'contained' : 'text'}
                      size="small"
                      onClick={() => setDateFilterPreset('CUSTOM')}
                      sx={{ justifyContent: 'flex-start', fontSize: '0.813rem', textTransform: 'none' }}
                    >
                      {dateFilterPresetLabels['CUSTOM']}
                    </Button>
                    {dateFilterPreset === 'CUSTOM' && (
                      <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={fr}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1 }}>
                          <DatePicker
                            label="Du"
                            value={customDateFrom}
                            onChange={(date) => setCustomDateFrom(date)}
                            slotProps={{ textField: { size: 'small', fullWidth: true } }}
                          />
                          <DatePicker
                            label="Au"
                            value={customDateTo}
                            onChange={(date) => setCustomDateTo(date)}
                            slotProps={{ textField: { size: 'small', fullWidth: true } }}
                          />
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() => setDatePopoverAnchor(null)}
                            sx={{ mt: 1 }}
                          >
                            Appliquer
                          </Button>
                        </Box>
                      </LocalizationProvider>
                    )}
                  </Box>
                </Box>
              </Popover>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Content based on view mode */}
      {viewMode === 'grouped' ? (
        /* Grouped View */
        <Box>
          {isLoading && <LinearProgress sx={{ mb: 2 }} />}

          {/* Pagination info for grouped view */}
          {proposalsData?.meta && (
            <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                Affichage de {proposalsData.meta.offset + 1} à {Math.min(proposalsData.meta.offset + proposalsData.meta.limit, proposalsData.meta.total)} sur {proposalsData.meta.total} propositions
              </Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  size="small"
                  disabled={paginationModel.page === 0}
                  onClick={() => setPaginationModel({ ...paginationModel, page: paginationModel.page - 1 })}
                >
                  Précédent
                </Button>
                <Button
                  size="small"
                  disabled={!proposalsData.meta.hasMore}
                  onClick={() => setPaginationModel({ ...paginationModel, page: paginationModel.page + 1 })}
                >
                  Suivant
                </Button>
              </Box>
            </Box>
          )}

          {sortedGroupKeys.map(groupKey => {
            const proposals = groupedProposals[groupKey]
            const isExpanded = expandedGroups.has(groupKey)
            return (
            <Accordion key={groupKey} expanded={isExpanded} sx={{ mb: 2 }}>
              <AccordionSummary
                onClick={(e) => {
                  // Empêcher l'expansion/contraction si on clique sur le contenu principal
                  const target = e.target as HTMLElement
                  const isExpandIcon = target.closest('.chevron-icon')
                  if (!isExpandIcon) {
                    e.preventDefault()
                    // Pour les propositions sans eventId (unmatched-*), naviguer vers la vue individuelle
                    if (groupKey.startsWith('unmatched-')) {
                      const proposalId = groupKey.replace('unmatched-', '')
                      window.location.href = `/proposals/${proposalId}`
                    } else {
                      // Naviguer vers la vue groupée
                      window.location.href = `/proposals/group/${encodeURIComponent(groupKey)}`
                    }
                  }
                }}
                sx={{
                  cursor: 'pointer',
                  '&:hover': {
                    backgroundColor: 'action.hover'
                  },
                  '& .MuiAccordionSummary-expandIconWrapper': {
                    display: 'none' // Masquer l'icône par défaut
                  }
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                  <IconButton
                    className="chevron-icon"
                    size="small"
                    onClick={(e) => handleToggleGroup(groupKey, e)}
                    sx={{
                      p: 0.5,
                      transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                      transition: 'transform 0.2s ease-in-out',
                      '&:hover': {
                        backgroundColor: 'action.selected'
                      }
                    }}
                  >
                    <ExpandMoreIcon />
                  </IconButton>
                  <Chip
                    size="small"
                    label={(proposals[0]?.groupCount || proposals.length).toString()}
                    color="primary"
                    sx={{
                      minWidth: '32px',
                      borderRadius: '12px',
                      fontWeight: 600
                    }}
                  />
                  <Typography variant="h6" sx={{ flexGrow: 1 }}>
                    {getEventTitle(groupKey, proposals)}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {/* Afficher les noms d'agents uniques */}
                    {[...new Set(proposals.map(p => p.agent.name))].map((agentName) => (
                      <Chip
                        key={agentName}
                        size="small"
                        label={agentName}
                        variant="outlined"
                        sx={{
                          bgcolor: 'action.selected',
                          borderColor: 'divider',
                          fontWeight: 500
                        }}
                      />
                    ))}
                    {/* Afficher les types uniques */}
                    {[...new Set(proposals.map(p => p.type))].map((type) => (
                      <Chip
                        key={type}
                        icon={getProposalTypeIcon(type as ProposalType)}
                        size="small"
                        label={proposalTypeLabels[type as ProposalType]}
                        sx={getProposalTypeStyle(type as ProposalType)}
                      />
                    ))}
                    {/* Afficher uniquement les statuts uniques */}
                    {[...new Set(proposals.map(p => p.status))].map((status) => {
                      const count = proposals.filter(p => p.status === status).length
                      return (
                        <Chip
                          key={status}
                          size="small"
                          label={count > 1 ? `${proposalStatusLabels[status as ProposalStatus]} (${count})` : proposalStatusLabels[status as ProposalStatus]}
                          color={proposalStatusColors[status as ProposalStatus]}
                        />
                      )
                    })}
                  </Box>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <List>
                  {proposals.map((proposal, index) => (
                    <React.Fragment key={proposal.id}>
                      <ListItem>
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                              <Chip
                                icon={getProposalTypeIcon(proposal.type as ProposalType)}
                                size="small"
                                label={proposalTypeLabels[proposal.type as ProposalType]}
                                sx={getProposalTypeStyle(proposal.type as ProposalType)}
                              />
                              <ProposalSourceChip
                                source={(proposal as any).sourceMetadata?.type}
                                milesEnv={(proposal as any).sourceMetadata?.extra?.milesEnv}
                              />
                              <Typography component="span" variant="body2" color="text.secondary">
                                par {proposal.agent.name}
                              </Typography>
                              <Typography component="span" variant="body2" color="text.secondary">
                                {proposal.confidence ? `${Math.round(proposal.confidence * 100)}% confiance` : ''}
                              </Typography>
                              <Typography component="span" variant="caption" color="text.secondary">
                                {new Date(proposal.createdAt).toLocaleDateString('fr-FR')}
                              </Typography>
                            </Box>
                          }
                          secondary={
                            <Box>
                              <Typography component="div" variant="body2" sx={{ display: 'block', mb: 1 }}>
                                {getChangesSummary(proposal.changes)}
                              </Typography>

                              {/* Affichage des changements principaux */}
                              {Object.entries(proposal.changes).map(([key, value]: [string, any]) => {
                                if (key === 'startDate' && typeof value === 'object' && value.new) {
                                  // Affichage spécial pour les dates de début
                                  const dateValue = new Date(value.new)
                                  const confidence = value.confidence ? Math.round(value.confidence * 100) : 0
                                  return (
                                    <Chip
                                      key={key}
                                      size="small"
                                      label={`Date proposée: ${dateValue.toLocaleDateString('fr-FR')} (${confidence}% confiance)`}
                                      variant="outlined"
                                      sx={{ mr: 1, mb: 0.5, backgroundColor: 'success.light', color: 'success.dark' }}
                                    />
                                  )
                                } else if (key === 'races' && Array.isArray(value)) {
                                  return (
                                    <Box key={key} sx={{ display: 'block', mt: 1 }}>
                                      <Chip
                                        size="small"
                                        label={`${value.length} course(s) incluse(s)`}
                                        variant="outlined"
                                        color="info"
                                        sx={{ mr: 1, mb: 0.5 }}
                                      />
                                      {/* Afficher un aperçu des courses */}
                                      <Typography component="div" variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                        Courses: {value.map((race: any) => race.raceName).slice(0, 3).join(', ')}
                                        {value.length > 3 && ` et ${value.length - 3} autre(s)...`}
                                      </Typography>
                                    </Box>
                                  )
                                } else if (typeof value === 'object' && value.field) {
                                  return (
                                    <Chip
                                      key={key}
                                      size="small"
                                      label={`${value.field}: ${value.current} → ${value.proposed}`}
                                      variant="outlined"
                                      sx={{ mr: 1, mb: 0.5 }}
                                    />
                                  )
                                } else if (groupKey.startsWith('new-event-')) {
                                  return (
                                    <Chip
                                      key={key}
                                      size="small"
                                      label={`${key}: ${typeof value === 'string' && value.includes('T') ?
                                        new Date(value).toLocaleDateString('fr-FR') :
                                        String(value)}`}
                                      variant="outlined"
                                      sx={{ mr: 1, mb: 0.5 }}
                                    />
                                  )
                                }
                                return null
                              })}
                            </Box>
                          }
                          secondaryTypographyProps={{ component: 'div' }}
                        />
                        <ListItemSecondaryAction>
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <Tooltip title="Voir détails">
                              <IconButton
                                size="small"
                                component={RouterLink}
                                to={`/proposals/${proposal.id}`}
                              >
                                <ViewIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            {proposal.status === 'PENDING' && (
                              <>
                                <Tooltip title="Approuver">
                                  <IconButton
                                    size="small"
                                    color="success"
                                  >
                                    <ApproveIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Rejeter">
                                  <IconButton
                                    size="small"
                                    color="error"
                                  >
                                    <RejectIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </>
                            )}
                          </Box>
                        </ListItemSecondaryAction>
                      </ListItem>
                      {index < proposals.length - 1 && <Divider />}
                    </React.Fragment>
                  ))}
                </List>
              </AccordionDetails>
            </Accordion>
            )
          })}

          {Object.keys(groupedProposals).length === 0 && !isLoading && (
            <Card>
              <CardContent sx={{ textAlign: 'center', py: 6 }}>
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  Aucune proposition trouvée
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Modifiez vos filtres ou attendez que les agents génèrent de nouvelles propositions.
                </Typography>
              </CardContent>
            </Card>
          )}
        </Box>
      ) : (
        /* Table View */
        <Card>
          <Box sx={{ height: 600 }}>
            {isLoading && <LinearProgress />}
            <DataGridPro
              rows={filteredProposals}
              columns={columns}
              pagination
              paginationMode="server"
              rowCount={proposalsData?.meta?.total || 0}
              paginationModel={paginationModel}
              onPaginationModelChange={setPaginationModel}
              pageSizeOptions={[25, 50, 100]}
              slots={{
                toolbar: GridToolbar,
              }}
              slotProps={{
                toolbar: {
                  showQuickFilter: true,
                  quickFilterProps: { debounceMs: 500 },
                },
              }}
              loading={isLoading}
              checkboxSelection
              rowSelectionModel={selectedRows}
              onRowSelectionModelChange={setSelectedRows}
              sx={{
                border: 'none',
                '& .MuiDataGrid-cell:focus': {
                  outline: 'none',
                },
                '& .MuiDataGrid-row:hover': {
                  backgroundColor: 'action.hover',
                },
              }}
            />
          </Box>
        </Card>
      )}
    </Box>
  )
}

export default ProposalList
